import DateTimeDisplay from '@/components/DateTime';
import {
  Badge,
  Box,
  Button,
  Card,
  Column,
  Table,
  toast,
  useBreakpoint,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { useMemo } from 'react';
import { generatePath, Link, useParams } from 'react-router-dom';

import { OrganizationSafeHarborPricingSettingsV2, OrganizationSettingsV2, SafeHarborPricingStatus } from 'types';

import {
  useGetOrganizationEquipmentSettingsQuery,
  useArchiveOrganizationEquipmentSettingMutation,
} from '../../../../../services/equipmentSettings';

import { CreateEquipmentSettingModal } from './CreateEquipmentSettingModal';

const numberFormatter = new Intl.NumberFormat('en-US');

const statusVariantMap: Record<SafeHarborPricingStatus, 'success' | 'warning'> = {
  [SafeHarborPricingStatus.active]: 'success',
  [SafeHarborPricingStatus.inactive]: 'warning',
};

export const EquipmentSettings = () => {
  const { alias = '', programType = '' } = useParams<{ alias: string; programType: string }>();
  const { isPhone } = useBreakpoint();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const { data, isLoading, isFetching } = useGetOrganizationEquipmentSettingsQuery(
    { alias, programType },
    { skip: !alias || !programType },
  );
  const [archiveEquipmentSetting] = useArchiveOrganizationEquipmentSettingMutation();

  const settings = useMemo(() => {
    if (!data?.length) {
      return [];
    }
    const settingsByStatus = data.reduce(
      (acc, setting) => {
        const status = setting.value?.status || SafeHarborPricingStatus.inactive;
        acc[status] = acc[status] || [];
        acc[status].push(setting);
        return acc;
      },
      {} as Record<string, OrganizationSettingsV2<OrganizationSafeHarborPricingSettingsV2>[]>,
    );
    const sortByEffectiveDateDesc = (
      a: OrganizationSettingsV2<OrganizationSafeHarborPricingSettingsV2>,
      b: OrganizationSettingsV2<OrganizationSafeHarborPricingSettingsV2>,
    ) => new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime();

    return [
      ...(settingsByStatus[SafeHarborPricingStatus.active] || []).toSorted(sortByEffectiveDateDesc),
      ...(settingsByStatus[SafeHarborPricingStatus.inactive] || []).toSorted(sortByEffectiveDateDesc),
    ];
  }, [data]);

  const handleArchive = async (settingId: string) => {
    try {
      await archiveEquipmentSetting({ alias, settingId, programType }).unwrap();
      toast.success('Equipment setting archived successfully');
    } catch (e) {
      console.error(e);
      toast.error('Error archiving equipment setting');
    }
  };

  const columnConfig: Column[] = [
    {
      heading: 'Status',
      render: (_, row) => (
        <Badge
          variant={statusVariantMap[row?.value.status as SafeHarborPricingStatus] || 'warning'}
          message={row?.value.status}
        />
      ),
    },
    {
      heading: 'SKU',
      render: (_, row) => row?.value.sku || '',
    },
    {
      heading: 'Org',
      dataKey: 'organizationId',
      render: (cell: any, row: any) => (
        <Link
          to={generatePath('/settings/organizations/:alias/pricing/:programType/equipmentSettings', {
            alias: row.organizationId,
            programType,
          })}
        >
          {cell}
        </Link>
      ),
    },
    {
      heading: 'Equipment Price',
      render: (_, row) => `$${numberFormatter.format(row?.value.equipmentPrice)}`,
    },
    {
      heading: 'Logistics Price',
      render: (_, row) => `$${numberFormatter.format(row?.value.shippingPrice)}`,
    },
    {
      heading: 'Effective Date',
      dataKey: 'effectiveDate',
      render: (cell: any) => <DateTimeDisplay value={cell} dateFormat={'DD'} excludeTime />,
    },
    {
      heading: 'Archived Date',
      render: (_, row) =>
        row?.value.inactiveDate ? (
          <DateTimeDisplay value={row.value.inactiveDate} dateFormat={'DD'} excludeTime />
        ) : (
          ''
        ),
    },
    {
      heading: '',
      render: (_cell: any, row: any) => {
        const isOwned = row?.organizationId === alias;
        return (
          isOwned &&
          row?.value?.status === SafeHarborPricingStatus.active && (
            <Button size="sm" variant="secondary" tone="neutral" onClick={() => handleArchive(row.id)}>
              Archive
            </Button>
          )
        );
      },
    },
  ];

  return (
    <>
      <Card.Header>
        <Box direction="row" justifyContent="space-between">
          <Box />
          <Button
            as="button"
            variant="primary"
            iconPrefix="add"
            size={{ base: 'sm', desktop: 'md' }}
            aria-label="create equipment setting"
            onClick={openModal}
          >
            {!isPhone && 'Equipment Setting'}
          </Button>
        </Box>
      </Card.Header>
      <Box margin="0 lg 0 lg">
        {!isLoading && !isFetching && settings.length === 0 ? (
          <Box padding="xl" display="flex" justifyContent="center" alignItems="center">
            <p>No equipment settings found.</p>
          </Box>
        ) : (
          <Table
            rowKey="_id"
            columns={columnConfig}
            rows={settings}
            isLoading={isLoading || isFetching}
            isScrollable={{ y: true }}
            hasStickyHeader
            aria-label="Equipment settings table"
            aria-describedby="equipment-settings-description"
          />
        )}
      </Box>
      <CreateEquipmentSettingModal
        isOpen={isModalOpen}
        onClose={closeModal}
        alias={alias}
        programType={programType}
      />
    </>
  );
};
